import React from 'react'
import classnames from 'classnames'
import _ from 'lodash'
import { inject, observer } from 'mobx-react'
import BoundaryList from './BoundaryList'
import { geoJSONBounds } from 'utils'

const BoundaryListSearchResult = ({
  selectionStore,
  uiStore,
  result,
  savingBoundaries
}) => {
  const finishedLoadingNode = result.loaded || result.zip_code_loaded
  const hasNoChildren = result.boundaries && result.boundaries.length === 0
  const isLeafNode = finishedLoadingNode && hasNoChildren
  let showByHasChildren = result.hasChildren
  if (typeof showByHasChildren === 'string') {
    showByHasChildren = showByHasChildren.toLowerCase() === 'true'
  }
  const shouldShowExpandIcon = isLeafNode
    ? false
    : (result.hasChildren !== undefined
        ? showByHasChildren
        : true)

  const isSelected = (selectionStore.item && selectionStore.item.selectedBoundaries || []).some(
    selectedBoundary => selectedBoundary.remote_db_id === result.id
  )

  const isHighlighted = selectionStore.item && selectionStore.item.selectedBoundary === result

  const handleExpand = (e) => {
    if (e) {
      e.stopPropagation()
    }
    result.expanded = !result.expanded
    if (result.expanded) {
      if (selectionStore.item && selectionStore.item.useZipCodes && !result.zip_code_loaded) {
        result.loadSubBoundaries(selectionStore.item.useZipCodes)
      }
      if (!result.loaded && selectionStore.item && !selectionStore.item.useZipCodes) {
        result.loadSubBoundaries(selectionStore.item.useZipCodes)
      }
    }
  }

  const handleSelectToggle = (e) => {
    e.stopPropagation()
    if (!selectionStore.item || typeof selectionStore.item.addBoundaryToSelection !== 'function') return
    if (e.target.checked) {
      const groupPk = selectionStore.data && selectionStore.data.group && selectionStore.data.group.pk
      selectionStore.item.addBoundaryToSelection({
        id: result.id,
        name: result.name,
        targetGroupId: groupPk,
        ids: result.ids,
        isZipCode: result.is_zip_code
      })
    } else {
      selectionStore.item.removeBoundaryFromSelection({ id: result.id })
    }
  }

  const handleRowClick = async (e) => {
    // Only zoom and render on map when clicking the item itself (not the checkbox or expand arrow)
    if (e.target.closest('.boundary-tree-checkbox') || e.target.closest('.boundary-tree-node__arrow')) {
      return
    }

    result.expanded = true
    if (selectionStore.item) {
      if (selectionStore.item.selectedBoundary) {
        selectionStore.item.selectedBoundary = null
      }
      selectionStore.item.selectedBoundary = result
    }

    if (result.bounds && result.bounds.length > 0) {
      uiStore.setBounds(result.bounds)
    }

    if (!result.geoJSON) {
      await result.loadGeometry()
    }
    if (selectionStore.item && selectionStore.item.useZipCodes && !result.zip_code_loaded) {
      await result.loadSubBoundaries(selectionStore.item.useZipCodes)
    }
    if (!result.loaded && selectionStore.item && !selectionStore.item.useZipCodes) {
      await result.loadSubBoundaries(selectionStore.item.useZipCodes)
    }

    if (result.bounds && result.bounds.length > 0) {
      uiStore.setBounds(result.bounds)
    } else if (result.geoJSON) {
      const calculatedBounds = geoJSONBounds(result.geoJSON)
      if (calculatedBounds) {
        uiStore.setBounds(calculatedBounds)
      }
    }
  }

  const parentHierarchy = result.parent_names
    ? result.parent_names.slice().reverse().join(', ')
    : null

  return (
    <li className='boundary-tree-node'>
      <div
        className={classnames('boundary-tree-node__row', {
          'is-selected-boundary': isHighlighted
        })}
        onClick={handleRowClick}
      >
        {/* Arrow expand or spinner or placeholder */}
        {result.fetching_data
          ? (
            <span className='boundary-tree-node__arrow-placeholder' style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <div className='spinner' style={{ width: 12, height: 12 }} />
            </span>
            )
          : shouldShowExpandIcon
            ? (
              <button
                type='button'
                className='boundary-tree-node__arrow'
                onClick={handleExpand}
                aria-label={result.expanded ? 'Collapse' : 'Expand'}
              >
                <svg
                  width='12'
                  height='12'
                  viewBox='0 0 24 24'
                  fill='none'
                  stroke='currentColor'
                  strokeWidth='2.5'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  style={{
                    transform: result.expanded ? 'rotate(90deg)' : 'rotate(0deg)',
                    transition: 'transform 0.15s ease'
                  }}
                >
                  <polyline points='9 18 15 12 9 6' />
                </svg>
              </button>
              )
            : (
              <span className='boundary-tree-node__arrow-placeholder' />
              )}

        {/* Checkbox: only selects item, does not zoom/render on map */}
        <input
          type='checkbox'
          className='boundary-tree-checkbox'
          checked={isSelected}
          onChange={handleSelectToggle}
          onClick={(e) => e.stopPropagation()}
        />

        {/* Polygon icon */}
        <span className='boundary-tree-node__polygon-icon'>
          <svg width='15' height='15' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.75' strokeLinecap='round' strokeLinejoin='round'>
            <path d='M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z' />
          </svg>
        </span>

        {/* Name and Parents */}
        <div className='boundary-tree-node__name-wrap'>
          <span className='boundary-tree-node__name' title={result.name}>
            {result.name}
          </span>
          {parentHierarchy && (
            <span className='boundary-tree-node__parents' title={parentHierarchy}>
              {parentHierarchy}
            </span>
          )}
        </div>
      </div>

      {/* Nested Children */}
      {result.expanded && !result.fetching_data && result.boundaries && result.boundaries.length > 0 && (
        <div className='boundary-tree-node__children'>
          <BoundaryList root={result} parentNode={result} savingBoundaries={savingBoundaries} />
        </div>
      )}
    </li>
  )
}

export default inject('selectionStore', 'uiStore')(
  observer(BoundaryListSearchResult)
)
